import React from "react";

interface NeuralConnectionProps {
  from: { x: number; y: number };
  to: { x: number; y: number };
  isActive: boolean;
  delay?: number;
  thickness?: 'thin' | 'medium' | 'thick';
}

export const NeuralConnection: React.FC<NeuralConnectionProps> = ({
  from,
  to,
  isActive,
  delay = 0,
  thickness = 'medium'
}) => {
  const strokeWidths = {
    thin: 1,
    medium: 2,
    thick: 3
  };

  // Calculate control points for smooth curves
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  // Create a gentle curve
  const curvature = Math.min(distance * 0.3, 20);
  const controlX1 = from.x + dx * 0.3 + dy * curvature * 0.01;
  const controlY1 = from.y + dy * 0.3 - dx * curvature * 0.01;
  const controlX2 = to.x - dx * 0.3 + dy * curvature * 0.01;
  const controlY2 = to.y - dy * 0.3 - dx * curvature * 0.01;

  const pathData = `M ${from.x} ${from.y} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${to.x} ${to.y}`;

  return (
    <g className="neural-connection">
      {/* Background path */}
      <path
        d={pathData}
        stroke="hsl(var(--primary) / 0.1)"
        strokeWidth={strokeWidths[thickness]}
        fill="none"
        className="transition-all duration-500"
      />
      
      {/* Active connection */}
      <path
        d={pathData}
        stroke="url(#neuralGradient)"
        strokeWidth={strokeWidths[thickness]}
        fill="none"
        strokeLinecap="round"
        className={`
          transition-all duration-700 ${isActive ? 'opacity-100' : 'opacity-0'}
        `}
        style={{ 
          animationDelay: `${delay}ms`,
          filter: isActive ? 'drop-shadow(0 0 6px hsl(var(--primary) / 0.6))' : 'none'
        }}
      />
      
      {/* Flowing particles */}
      {isActive && (
        <>
          <circle
            r="2"
            fill="hsl(var(--primary))"
            className="animate-neural-pulse"
            style={{ animationDelay: `${delay}ms` }}
          >
            <animateMotion
              dur="2s"
              repeatCount="indefinite"
              begin={`${delay}ms`}
            >
              <mpath href={`#path-${from.x}-${from.y}-${to.x}-${to.y}`} />
            </animateMotion>
          </circle>
          
          <circle
            r="1.5"
            fill="hsl(var(--accent))"
            className="animate-neural-pulse"
            style={{ animationDelay: `${delay + 500}ms` }}
          >
            <animateMotion
              dur="3s"
              repeatCount="indefinite"
              begin={`${delay + 500}ms`}
            >
              <mpath href={`#path-${from.x}-${from.y}-${to.x}-${to.y}`} />
            </animateMotion>
          </circle>
        </>
      )}
      
      {/* Hidden path for animation reference */}
      <defs>
        <path
          id={`path-${from.x}-${from.y}-${to.x}-${to.y}`}
          d={pathData}
        />
      </defs>
    </g>
  );
};